import { ColumnType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import Template1 from "./ReportTemplates/Template1";
import { useLocation, useNavigate } from "react-router";
import { projectsApis } from "../../api/projects";
import dayjs from "dayjs";
import { TooltipComponent } from "../../components/Component";
import { Progress, Tooltip } from "antd";
import { useWbsName } from "../ProjectScheduleManagement/shared/useWbsCodes";

const ActivityLevelProgressSummaryReport = () => {
  const [statusFilterData, setStatusFilterData] = useState([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const wbsName = useWbsName();

  // Scroll to top when component mounts or when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const columns: ColumnType<any>[] = [
    {
      title: "Project Name",
      dataIndex: "projectName",
      key: "projectName",
      width: 300,
      fixed: "left",
    },
    {
      title: "Phase Name",
      dataIndex: "phaseName",
      key: "phaseName",
      width: 250,
      render: (text: any) => wbsName(text),
    },
    {
      title: "Activity Name",
      dataIndex: "activityName",
      key: "activityName",
      width: 250,
      render: (text: any, record: any) => (
        <>
          <span
            className="inlineTableCellLink"
            id={`activity-${record?.activityId}`}
            onClick={() => {
              navigate("/pms/task-completion-report", {
                state: {
                  ...location.state,
                  activityIds: [record.activityId],
                  activityName: text,
                },
              });
            }}
          >
            {wbsName(text)}
          </span>
          <TooltipComponent
            iconClass="info-circle text-info"
            icon={""}
            id={`activity-${record?.activityId}`}
            direction="right"
            text={"Click To View Tasks in " + wbsName(text)}
            containerClassName={"mt-1"}
          />
        </>
      ),
    },
    {
      title: "Planned Start",
      dataIndex: "plannedStartDate",
      key: "plannedStartDate",
      width: 100,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Planned End",
      dataIndex: "plannedEndDate",
      key: "plannedEndDate",
      width: 100,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Actual Start",
      dataIndex: "actualStartDate",
      key: "actualStartDate",
      width: 100,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
    {
      title: "Actual End",
      dataIndex: "actualEndDate",
      key: "actualEndDate",
      width: 100,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
    {
      title: "Planned Hours",
      dataIndex: "plannedHours",
      key: "plannedHours",
      width: 120,
    },
    {
      title: "Logged Hours",
      dataIndex: "loggedHours",
      key: "loggedHours",
      width: 120,
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Completed Percentage</span>,
      dataIndex: "tasksCompletionPercentage",
      key: "tasksCompletionPercentage",
      width: 200,
      render: (text: any) => {
        if (text === null || text === undefined || text === "") {
          return "N/A";
        }

        const percentage = typeof text === "string" ? parseFloat(text) : text;

        return (
          <Tooltip title={`Completion: ${percentage}%`} placement="top">
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={percentage}
              status="active"
              strokeColor={{
                from: "#87d068",
                to: "#87d068",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Budget Progress",
      dataIndex: "budgetConsumption",
      key: "budgetConsumption",
      width: 200,
      render: (data: any, record: any) => {
        const consumed = Number(data) || 0;
        const planned = Number(record?.budgetPlanned) || 0;
        const fmt = (n: number) =>
          `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
        if (planned <= 0) return consumed > 0 ? fmt(consumed) : "N/A";
        const percent = Math.round((consumed / planned) * 100);
        return (
          <Tooltip
            title={`${fmt(consumed)} of ${fmt(planned)} (${percent}%)`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={Math.min(100, percent)}
              status={percent > 100 ? "exception" : "active"}
              strokeColor={
                percent > 100
                  ? "#ff4d4f"
                  : { from: "#1677ff", to: "#1677ff" }
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      fixed: "right",
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
  ];

  const fetchStatusFilterData = async () => {
    try {
      const response = await projectsApis.fetchStatusFiltersByModule(
        "Activity"
      );
      const { data, status } = response;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item,
            value: item,
          };
        });
        setStatusFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  useEffect(() => {
    fetchStatusFilterData();
    dispatch(
      updateMenuData([
        {
          text: "Activity Level Progress Summary Report",
          link: "/pms/activity-level-progress-summary-report",
        },
      ])
    );
  }, []);

  // No need for additional clearing logic - ReportsNav now passes clean state

  return (
    <>
      <Template1
        moduleKey={window.location.pathname}
        columns={columns}
        pageTitle="Activity Level Progress Summary Report"
        pageDescription="Track detailed progress of activities within phases"
        previousPath={{
          link: location.state?.phaseIds
            ? "/pms/phase-level-progress-summary-report"
            : "/pms/project/reports",
          state: location.state?.phaseIds
            ? {
                id: location.state.id,
                projectName: location.state.projectName,
                // Clear phaseIds when going back to phase level to show all phases
              }
            : {
                id: location.state?.id,
                projectName: location.state?.projectName,
              },
        }}
        filters={{
          customFilters: [
            {
              type: "multiple_selection",
              name: "activityStatus",
              label: "Status",
              options: statusFilterData,
            },
          ],
        }}
      />
    </>
  );
};

export default ActivityLevelProgressSummaryReport;
